// Maps Twilio Voice SDK error codes to clinician-readable copy for the call
// card. The SDK's own messages ("UnknownError (31000)") are meaningless to the
// people placing calls, so the card shows the mapped copy and keeps the raw
// code as small print for support triage; the full SDK error still goes to the
// client logs verbatim via describeTwilioError.
//
// Codes: https://www.twilio.com/docs/api/errors — the subsets the browser SDK
// actually emits are the 20xxx token errors, the 31xxx client/signaling
// errors, and the 53xxx connection errors.

const TOKEN_COPY = 'Your calling session could not be authorized. Refresh the page and try again.';
const MIC_BLOCKED_COPY = 'Microphone access is blocked. Allow microphone access for this site in your browser, then try again.';
const MIC_FAILED_COPY = 'Could not access your microphone. Check that it is connected and not in use by another app, then try again.';
// Exported for the controller's preflight-timeout path — the one mic failure
// with no getUserMedia rejection (and so no code) to map. Second sentence
// deliberately matches MIC_BLOCKED_COPY so the remediation reads the same.
export const MIC_TIMEOUT_COPY =
    'Timed out waiting for microphone access. Allow microphone access for this site in your browser, then try again.';
const NETWORK_COPY = 'Connection to the calling service was lost. Check your internet connection and try again.';
const MEDIA_COPY =
    'Call audio could not be established. Check your network connection (a VPN or firewall may be blocking audio) and try again.';

const TWILIO_ERROR_COPY = {
    // Access-token problems (fetched fresh per call, so these usually mean a
    // clock/session issue rather than staleness).
    20101: TOKEN_COPY, // AccessTokenInvalid
    20104: TOKEN_COPY, // AccessTokenExpired
    31204: TOKEN_COPY, // Invalid JWT token
    31205: TOKEN_COPY, // JWT token expired

    // Microphone. 31208 is the twilio.js 1.x code, 31401/31402 the voice-sdk
    // 2.x equivalents — keep both in case the SDK maps either through.
    31208: MIC_BLOCKED_COPY, // PermissionDeniedError (legacy)
    31401: MIC_BLOCKED_COPY, // UserMediaErrors.PermissionDeniedError
    31402: MIC_FAILED_COPY, // UserMediaErrors.AcquisitionFailedError

    // Signaling / transport between the browser and Twilio.
    // 31005 deliberately does NOT get the check-your-internet copy: in
    // production it is almost always "Error sent from gateway in HANGUP" —
    // Twilio terminating the call from its side (dial-leg failure), not the
    // clinician's network. These carry a CallSid, so the post-error server
    // poll upgrades the message to the real reason when the row went FAILED.
    31005: 'The call was disconnected unexpectedly. Try again; if it keeps happening, contact support with the error code below.',
    31009: NETWORK_COPY, // SignalingErrors.TransportError
    53000: NETWORK_COPY, // Signaling connection error
    53001: NETWORK_COPY, // Signaling connection disconnected
    53405: MEDIA_COPY, // Media connection failed (ICE)

    // Call setup outcomes. 31000 is Twilio's catch-all for server-side dial
    // rejections (unroutable number, geo permissions) — the ticket's
    // "UnknownError (31000)". 31002 is what our own TwiML <Reject/> surfaces
    // as (patient has no phone number on file, calling disabled for clinic).
    31000: 'The call could not be completed. The number may not be reachable from our system — verify the number and try again. If this keeps happening, contact support with the error code below.',
    31002: 'The call was refused. The patient may not have a dialable phone number on file, or outbound calling may not be enabled for this clinic.',
    31003: 'The call timed out before it could connect. Try again.',

    // Carrier-level outcomes (SIP-mapped).
    31480: 'The number could not be reached. It may be switched off or out of service — try again later.',
    31486: 'The line is busy. Try again later.',
    31603: 'The call was declined by the other end.'
};

const UNRECOGNIZED_CODE_COPY = 'The call failed unexpectedly. Try again; if it keeps happening, contact support with the error code below.';

// Server-persisted dial failures (Twilio 132xx TwiML errors). These never
// reach the browser SDK — Twilio's status callback writes them onto the
// OutboundCall row and the controller fetches them after a terminal SDK error
// to replace the generic 31000-era copy. Entries take the dialed number so
// the copy can name it.
const SERVER_DIAL_ERROR_COPY = {
    13223: (num) => `${num || 'This number'} is not in a dialable format. Correct the phone number and try again.`,
    13224: (num) => `${num || 'This number'} cannot be dialed — it may be invalid or out of service. Verify the phone number.`,
    13225: (num) => `Calls to ${num || 'this number'} are blocked (premium or restricted number).`,
    13227: () => 'Calls to this number’s region are not enabled for our account. Contact support to enable it.'
};

// A FAILED row with no ErrorCode means Twilio's callback carried none — the
// carrier rejected the dial without a specific reason.
const CARRIER_REJECTED_COPY = 'The carrier rejected the call. Verify the phone number and try again.';

// Terminal outcomes on the dialed leg that aren't failures at all. Twilio's
// gateway can attach a generic 31000 to the HANGUP of an ordinary unanswered
// dial, which lands the call in the error card with number-blaming copy — but
// the status callbacks have already recorded what really happened. When the
// server says the dialed leg simply ended NO_ANSWER or BUSY, the outcome is
// the honest copy. `code` is null on purpose: there was no error, so the
// card's TW- small print (and the SDK's misleading 31000) should disappear.
const SERVER_OUTCOME_COPY = {
    NO_ANSWER: 'The call was not answered. Try again later.',
    BUSY: 'The line is busy. Try again later.'
};

/**
 * Map a server-confirmed non-failure outcome on the dialed (child) leg to
 * card copy, or null when the outcome isn't one we can say anything more
 * honest about (COMPLETED / CANCELED — keep the SDK's copy).
 *
 * @param {string | null | undefined} childStatus
 * @returns {{ message: string, code: null } | null}
 */
export function friendlyServerOutcome(childStatus) {
    const message = SERVER_OUTCOME_COPY[childStatus];
    if (message) {
        return { message, code: null };
    }
    return null;
}

/**
 * Map the server's persisted view of a FAILED OutboundCall row to card copy.
 * Always returns a payload — the caller has already established the call
 * failed; this only decides how specifically we can say why.
 *
 * @param {{ errorCode: string | number | null | undefined, toNumber?: string | null }} row
 * @returns {{ message: string, code: number | null }}
 */
export function friendlyServerCallError({ errorCode, toNumber }) {
    const code = Number.parseInt(errorCode, 10);
    if (Number.isFinite(code) && SERVER_DIAL_ERROR_COPY[code]) {
        return { message: SERVER_DIAL_ERROR_COPY[code](toNumber), code };
    }
    if (Number.isFinite(code)) {
        return { message: UNRECOGNIZED_CODE_COPY, code };
    }
    return { message: CARRIER_REJECTED_COPY, code: null };
}

/**
 * Map a getUserMedia rejection (from the controller's mic preflight) to the
 * callError payload. NotAllowedError/PermissionDeniedError → 31401 and the
 * fall-through (no mic, mic in use, hardware failure) → 31402 mirror the
 * Twilio SDK's own classification in call.accept(). SecurityError is a
 * deliberate divergence: the SDK would lump it into 31402
 * (AcquisitionFailedError), but it means mic access is blocked by policy
 * (insecure context, permissions policy) — not a flaky device — so the
 * blocked copy is the accurate one.
 *
 * @param {unknown} err DOMException (or plain error) from getUserMedia
 * @returns {{ message: string, code: number }}
 */
export function friendlyUserMediaError(err) {
    if (err?.name === 'NotAllowedError' || err?.name === 'PermissionDeniedError' || err?.name === 'SecurityError') {
        return { message: MIC_BLOCKED_COPY, code: 31401 };
    }
    return { message: MIC_FAILED_COPY, code: 31402 };
}

/**
 * Convert a Twilio SDK (or plain) error into the callError payload the slice
 * stores: mapped copy when the code is known, generic copy + the raw code when
 * it isn't, and the error's own message only when there is no code at all
 * (non-Twilio failure, e.g. a thrown setup error).
 *
 * @param {unknown} err
 * @param {string} fallbackMessage shown when err carries neither code nor message
 * @returns {{ message: string, code: number | null }}
 */
export function friendlyCallError(err, fallbackMessage = 'Call failed') {
    const code = typeof err?.code === 'number' ? err.code : null;
    if (code !== null && TWILIO_ERROR_COPY[code]) {
        return { message: TWILIO_ERROR_COPY[code], code };
    }
    if (code !== null) {
        return { message: UNRECOGNIZED_CODE_COPY, code };
    }
    return { message: err?.message || fallbackMessage, code: null };
}

/**
 * Decompose a Twilio SDK error into a plain loggable object. TwilioError
 * carries diagnostic fields (causes, description, explanation, originalError)
 * beyond message/code — losing them was why 31000 tickets were undebuggable
 * from the logs. undefined fields drop out of the JSON batch payload.
 *
 * @param {unknown} err
 */
export function describeTwilioError(err) {
    return {
        error: err?.message,
        code: err?.code ?? null,
        name: err?.name ?? undefined,
        description: err?.description ?? undefined,
        explanation: err?.explanation ?? undefined,
        causes: Array.isArray(err?.causes) && err.causes.length > 0 ? err.causes : undefined,
        originalError: err?.originalError?.message ?? undefined
    };
}
